import { useEffect, useMemo, useState } from "react";
import { Button } from "../ui/button";
import { AsyncButton } from "../ui/async-button";
import { Checkbox } from "../ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import type { PublishSettings, TrimSize } from "../../../shared/publishFormattingShared";
import { TRIM_SIZE_LABELS } from "../../../shared/publishFormattingShared";
import type { PreflightReport } from "../../../shared/preflight";
import {
  PUBLISH_THEME_PRESETS,
  applyPublishTheme,
  type PublishThemePreset,
} from "../../../shared/publishThemes";
import type { PublishReadinessResult } from "../../../shared/publishReadiness";
import { cn } from "../../lib/utils";

const WIZARD_STEPS = ["Theme", "Trim size", "Preflight", "Export"] as const;
type WizardStep = 0 | 1 | 2 | 3;

const TRIM_OPTIONS: TrimSize[] = ["5x8", "5.5x8.5", "6x9", "8x10", "8.5x11", "A5", "A4"];

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  settings: PublishSettings;
  onApplySettings: (next: PublishSettings) => void;
  preflight: PreflightReport | null;
  onExportPdf: () => void;
  exporting?: boolean;
  /** Soft beta-readiness gate — export still allowed after acknowledgment. */
  readiness?: PublishReadinessResult | null;
};

export function PublishWizard({
  open,
  onOpenChange,
  settings,
  onApplySettings,
  preflight,
  onExportPdf,
  exporting,
  readiness,
}: Props) {
  const [step, setStep] = useState<WizardStep>(0);
  const [pickedThemeId, setPickedThemeId] = useState<string | null>(null);
  const [ackReadiness, setAckReadiness] = useState(false);

  const activeTheme: PublishThemePreset | undefined = useMemo(
    () => PUBLISH_THEME_PRESETS.find((t) => t.id === pickedThemeId),
    [pickedThemeId]
  );

  const readinessItems = useMemo(() => {
    const blockers = readiness?.blockers ?? [];
    const warnings = readiness?.warnings ?? [];
    return { blockers, warnings, hasIssues: blockers.length + warnings.length > 0 };
  }, [readiness]);

  useEffect(() => {
    if (!open) {
      setAckReadiness(false);
      setStep(0);
    }
  }, [open]);

  function selectTheme(themeId: string) {
    setPickedThemeId(themeId);
    onApplySettings(applyPublishTheme(settings, themeId));
  }

  function setTrim(trim: TrimSize) {
    onApplySettings({ ...settings, trimSize: trim, platform: "custom" });
  }

  function close() {
    onOpenChange(false);
    setStep(0);
    setAckReadiness(false);
  }

  const exportDisabled =
    Boolean(exporting) || (readinessItems.hasIssues && !ackReadiness);

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(true) : close())}>
      <DialogContent className="sm:max-w-lg" showCloseButton>
        <DialogHeader>
          <DialogTitle>Publish wizard</DialogTitle>
          <DialogDescription>
            Step {step + 1} of {WIZARD_STEPS.length}: {WIZARD_STEPS[step]}
          </DialogDescription>
        </DialogHeader>

        <div className="mb-3 flex gap-1.5" aria-label="Wizard steps">
          {WIZARD_STEPS.map((label, i) => (
            <Button
              key={label}
              type="button"
              variant={step === i ? "secondary" : "outline"}
              size="sm"
              className="h-auto min-w-0 flex-1 whitespace-normal py-2 text-[0.6875rem]"
              aria-current={step === i ? "step" : undefined}
              onClick={() => setStep(i as WizardStep)}
            >
              {i + 1}. {label}
            </Button>
          ))}
        </div>

        {step === 0 ? (
          <div className="flex flex-wrap gap-2">
            {PUBLISH_THEME_PRESETS.map((theme) => (
              <Button
                key={theme.id}
                type="button"
                variant={pickedThemeId === theme.id ? "secondary" : "outline"}
                className="h-auto min-w-[46%] flex-1 basis-[46%] flex-col whitespace-normal py-2 text-center text-[0.8rem] leading-snug"
                aria-pressed={pickedThemeId === theme.id}
                onClick={() => selectTheme(theme.id)}
              >
                {theme.name}
                <span className="block text-[0.72rem] font-normal text-muted-foreground">
                  {theme.description}
                </span>
              </Button>
            ))}
            {activeTheme ? (
              <p className="w-full text-xs leading-snug text-muted-foreground">
                Applied: {activeTheme.name}
              </p>
            ) : (
              <p className="w-full text-xs leading-snug text-muted-foreground">
                Pick a theme to restyle typography and chapter chrome.
              </p>
            )}
          </div>
        ) : null}

        {step === 1 ? (
          <div>
            <p className="mb-2 text-xs leading-snug text-muted-foreground">Choose trim size for print.</p>
            <div className="flex flex-wrap gap-2">
              {TRIM_OPTIONS.map((trim) => (
                <Button
                  key={trim}
                  type="button"
                  variant={settings.trimSize === trim ? "secondary" : "outline"}
                  size="sm"
                  className="h-auto min-w-24 flex-1 whitespace-normal py-2 text-center"
                  aria-pressed={settings.trimSize === trim}
                  onClick={() => setTrim(trim)}
                >
                  {TRIM_SIZE_LABELS[trim]}
                </Button>
              ))}
            </div>
          </div>
        ) : null}

        {step === 2 ? (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              {preflight?.ok
                ? `Ready · ${preflight.warningCount} warning${preflight.warningCount === 1 ? "" : "s"}`
                : `${preflight?.errorCount ?? 0} blocking · ${preflight?.warningCount ?? 0} warning${(preflight?.warningCount ?? 0) === 1 ? "" : "s"}`}
            </p>
            <ul className="flex max-h-45 list-none flex-col gap-2 overflow-auto p-0">
              {(preflight?.issues ?? []).slice(0, 12).map((issue) => (
                <li
                  key={`${issue.code}-${issue.message}`}
                  className="flex items-start gap-2 rounded-md border border-border bg-muted/25 px-2 py-1.5"
                >
                  <span
                    className={cn(
                      "shrink-0 rounded px-1.5 py-0.5 text-[0.65rem] font-bold uppercase tracking-wide",
                      issue.level === "error" && "bg-destructive/15 text-destructive",
                      issue.level === "warning" && "bg-amber-500/15 text-amber-700 dark:text-amber-300",
                      issue.level === "info" && "bg-muted text-muted-foreground"
                    )}
                  >
                    {issue.level}
                  </span>
                  <div>
                    <div className="text-sm text-foreground">{issue.message}</div>
                  </div>
                </li>
              ))}
            </ul>
            {readinessItems.hasIssues ? (
              <div
                className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-[0.72rem] leading-snug"
                role="status"
              >
                <p className="mb-1 font-medium text-foreground">Beta-readiness</p>
                {readinessItems.blockers.length > 0 ? (
                  <ul className="mb-2 list-disc pl-4 text-amber-900 dark:text-amber-100">
                    {readinessItems.blockers.map((b) => (
                      <li key={b}>{b}</li>
                    ))}
                  </ul>
                ) : null}
                {readinessItems.warnings.length > 0 ? (
                  <ul className="list-disc pl-4 text-muted-foreground">
                    {readinessItems.warnings.map((w) => (
                      <li key={w}>{w}</li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ) : (
              <p className="text-[0.72rem] leading-snug text-muted-foreground">
                Beta-readiness looks clear — invite readers still beats polish when drafting.
              </p>
            )}
          </div>
        ) : null}

        {step === 3 ? (
          <div className="space-y-3">
            <p className="text-xs leading-snug text-muted-foreground">
              Export your interior PDF when preflight looks good. You can keep tweaking settings in
              the formatter tabs after closing the wizard.
            </p>
            {readinessItems.hasIssues ? (
              <div
                className="rounded-md border border-border bg-muted/30 px-3 py-2 text-[0.72rem] leading-snug space-y-2"
                role="note"
              >
                <p className="font-medium text-foreground">Readiness before store upload</p>
                {readinessItems.blockers.length > 0 ? (
                  <ul className="list-disc pl-4 text-destructive">
                    {readinessItems.blockers.map((b) => (
                      <li key={b}>{b}</li>
                    ))}
                  </ul>
                ) : null}
                {readinessItems.warnings.length > 0 ? (
                  <ul className="list-disc pl-4 text-muted-foreground">
                    {readinessItems.warnings.map((w) => (
                      <li key={w}>{w}</li>
                    ))}
                  </ul>
                ) : null}
                <label className="flex cursor-pointer items-start gap-2 text-foreground">
                  <Checkbox
                    className="mt-0.5"
                    checked={ackReadiness}
                    onCheckedChange={(value) => setAckReadiness(value === true)}
                  />
                  <span>I understand readiness warnings</span>
                </label>
              </div>
            ) : (
              <div
                className="rounded-md border border-border bg-muted/30 px-3 py-2 text-[0.72rem] leading-snug text-muted-foreground"
                role="note"
              >
                Beta-readiness gate: before you upload to stores, invite readers and clear Story Craft
                blockers on weak chapters. Format is the last mile — not the first.
              </div>
            )}
            <AsyncButton
              type="button"
              className="w-full font-semibold"
              onClick={onExportPdf}
              busy={exporting}
              busyLabel="Exporting…"
              disabled={exportDisabled}
              title={
                readinessItems.hasIssues && !ackReadiness
                  ? "Acknowledge readiness warnings to enable export"
                  : undefined
              }
            >
              Export Publication PDF
            </AsyncButton>
          </div>
        ) : null}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={close}>
            Close
          </Button>
          {step > 0 ? (
            <Button type="button" variant="secondary" onClick={() => setStep((s) => (s - 1) as WizardStep)}>
              Back
            </Button>
          ) : null}
          {step < 3 ? (
            <Button type="button" onClick={() => setStep((s) => (s + 1) as WizardStep)}>
              Next
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Compact theme strip for the Publish panel. */
export function PublishThemeStrip({
  onApplyTheme,
}: {
  onApplyTheme: (themeId: string) => void;
}) {
  return (
    <div aria-label="Publish themes">
      <p className="mb-2 text-xs leading-snug text-muted-foreground">
        Themes restyle fonts and chapter chrome without changing platform presets.
      </p>
      <div className="flex flex-wrap gap-2">
        {PUBLISH_THEME_PRESETS.map((theme) => (
          <Button
            key={theme.id}
            type="button"
            variant="outline"
            className="h-auto min-w-24 flex-1 flex-col whitespace-normal py-2 text-center text-[0.8rem] leading-snug"
            onClick={() => onApplyTheme(theme.id)}
            title={theme.description}
          >
            {theme.name}
            <span className="block text-[0.72rem] font-normal text-muted-foreground">
              {theme.description}
            </span>
          </Button>
        ))}
      </div>
    </div>
  );
}
